import { useCallback, useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { adminAPI } from "@/services/api";
import { CheckCircle2, ClipboardList, Clock, RefreshCw, XCircle } from "lucide-react";
import {
  TailAdminDataTable,
  TailAdminEmptyState,
  TailAdminMetricCard,
  TailAdminPageHeader,
  TailAdminPanel,
  TailAdminStatusBadge,
} from "@/components/template/tailadmin/DashboardPrimitives";

const statusTone = {
  active: "success",
  pending: "warning",
  churned: "danger",
};

const statusIcon = {
  active: CheckCircle2,
  pending: Clock,
  churned: XCircle,
};

const formatDate = (value) => {
  if (!value) return "-";
  return new Date(value).toLocaleDateString("en-NG", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

const AdminEnrollments = () => {
  const [enrollments, setEnrollments] = useState([]);
  const [total, setTotal] = useState(0);
  const [statusFilter, setStatusFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  const fetchEnrollments = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminAPI.getEnrollments(statusFilter !== "all" ? statusFilter : null);
      setEnrollments(res.data?.enrollments || []);
      setTotal(res.data?.total || 0);
    } catch {
      toast.error("Failed to load enrollments");
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    fetchEnrollments();
  }, [fetchEnrollments]);

  const summary = useMemo(() => ({
    active: enrollments.filter((item) => item.status === "active").length,
    pending: enrollments.filter((item) => item.status === "pending").length,
    churned: enrollments.filter((item) => item.status === "churned").length,
  }), [enrollments]);

  const rows = enrollments.map((item) => {
    const StatusIcon = statusIcon[item.status] || ClipboardList;
    return {
      id: item.enrollmentId || item.id,
      cells: [
        <span className="font-mono text-xs font-semibold text-gray-900">{item.userMasked || "Not available"}</span>,
        <span className="font-medium text-gray-700">{item.product?.name || "No product"}</span>,
        <span className="text-gray-600">{item.brm?.name || "Unassigned"}</span>,
        <TailAdminStatusBadge tone={statusTone[item.status] || "neutral"}>
          <span className="inline-flex items-center gap-1">
            <StatusIcon className="h-3.5 w-3.5" />
            {item.status || "unknown"}
          </span>
        </TailAdminStatusBadge>,
        <span className="text-gray-500">{formatDate(item.enrolledAt)}</span>,
        <span className="text-gray-500">{formatDate(item.subscribedAt)}</span>,
      ],
    };
  });

  return (
    <div className="space-y-6">
      <TailAdminPageHeader
        eyebrow="Platform proof"
        title="Enrollments"
        description="Audit enrollment records across all clients and BRMs."
        actions={
          <>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="h-11 w-44">
                <SelectValue placeholder="Filter status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="churned">Churned</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={fetchEnrollments} disabled={loading}>
              <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </Button>
          </>
        }
      />

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <TailAdminMetricCard label="Total" value={total.toLocaleString()} icon={ClipboardList} tone="primary" helper="All records" loading={loading} />
        <TailAdminMetricCard label="Active" value={summary.active.toLocaleString()} icon={CheckCircle2} tone="success" helper="Verified" loading={loading} />
        <TailAdminMetricCard label="Pending" value={summary.pending.toLocaleString()} icon={Clock} tone="warning" helper="Awaiting proof" loading={loading} />
        <TailAdminMetricCard label="Churned" value={summary.churned.toLocaleString()} icon={XCircle} tone="danger" helper="No longer active" loading={loading} />
      </div>

      <TailAdminPanel title="Enrollment ledger" description={`${enrollments.length} records loaded for current filter.`}>
        {loading || rows.length > 0 ? (
          <TailAdminDataTable
            loading={loading}
            columns={["User", "Product", "BRM", "Status", "Enrolled", "Subscribed"]}
            rows={rows}
          />
        ) : (
          <TailAdminEmptyState icon={ClipboardList} title="No enrollments found" description="No records match the selected status filter." />
        )}
      </TailAdminPanel>
    </div>
  );
};

export default AdminEnrollments;
